import { GridRowId } from '@mui/x-data-grid-pro';

import { GatewayPagamento } from 'components/autocompletes/GatewayPagamentoAutoComplete';
import { CondicaoPagamento } from 'pages/complementar/condicao-pagamento/types';
import { useConfigPedidoStepContext } from 'pages/compra/configuracao-pedido/contexts';
import { CondicaoPagamentoLoja, ConfigPedidoCondicoesPagamento, LojasProps } from 'pages/compra/configuracao-pedido/types';

/**
 * Filtra as condições de pagamento de acordo com os gateways de pagamento informados.
 */
const filterByGateway = (gateways: GatewayPagamento[], conditions: CondicaoPagamento[]): CondicaoPagamento[] => {
    const enumSet = new Set(gateways.map((g) => g.gatewayPagamentoEnum));

    return conditions.filter((c) => enumSet.has(c.gatewayPagamento?.gatewayPagamentoEnum as GatewayPagamento['gatewayPagamentoEnum']));
};

/**
 * Remove as condições de pagamento da loja que não estão mais na lista geral.
 */
const removeFromLojas = (lojas: CondicaoPagamentoLoja[], idsToRemove: number[]): CondicaoPagamentoLoja[] =>
    lojas.map((loja) => ({
        loja: loja.loja,
        condicaoPagamentoList: loja.condicaoPagamentoList.filter((c) => !idsToRemove.includes(c.idCondicaoPagamento as number)),
    }));

export function useActionsCondicoesPagamento({
    values,
    setValues,
}: {
    values: ConfigPedidoCondicoesPagamento;
    setValues: (values: ConfigPedidoCondicoesPagamento) => void;
}) {
    const { setActiveStepDirty } = useConfigPedidoStepContext();

    /**
     * Atualiza os valores e marca o passo como sujo.
     */
    const update = (newValues: ConfigPedidoCondicoesPagamento) => {
        setActiveStepDirty();
        setValues(newValues);
    };

    /**
     * Filtra condições gerais e por loja de acordo com os gateways selecionados.
     */
    const onGatewayChange = (gateways: GatewayPagamento[]) => {
        update({
            gatewayPagamento: gateways,
            condicaoPagamentoGeral: filterByGateway(gateways, values.condicaoPagamentoGeral),
            condicaoPagamentoLoja: values.condicaoPagamentoLoja.map((loja) => ({
                loja: loja.loja,
                condicaoPagamentoList: filterByGateway(gateways, loja.condicaoPagamentoList),
            })),
        });
    };

    /**
     * Atualiza as condições gerais e remove das lojas as condições que não estão mais na lista geral.
     */
    const onGeneralConditionsChange = (condicoesPagamento: CondicaoPagamento[]) => {
        const removedIds = condicoesPagamento.map((c) => c.idCondicaoPagamento as number);

        update({
            ...values,
            condicaoPagamentoGeral: condicoesPagamento,
            condicaoPagamentoLoja: removeFromLojas(values.condicaoPagamentoLoja, removedIds),
        });
    };

    /**
     * Adiciona lojas novas (ignora duplicatas).
     */
    const addLojas = (selectedLojas: LojasProps[]) => {
        const newLojas: CondicaoPagamentoLoja[] = selectedLojas
            .filter((selected) => !values.condicaoPagamentoLoja.some((loja) => loja.loja.cdLoja === selected.cdLoja))
            .map((selected) => ({ loja: selected, condicaoPagamentoList: [] }));

        update({
            ...values,
            condicaoPagamentoLoja: [...values.condicaoPagamentoLoja, ...newLojas],
        });
    };

    /**
     * Remove lojas pelo cdLoja.
     */
    const removeLojas = (cdsLoja: Set<GridRowId>) => {
        update({
            ...values,
            condicaoPagamentoLoja: values.condicaoPagamentoLoja.filter((loja) => !cdsLoja.has(loja.loja.cdLoja)),
        });
    };

    /**
     * Atualiza as condições de pagamento de uma loja específica.
     */
    const updateLojaConditions = (cdLoja: number | null, condicoesPagamento: CondicaoPagamento[]) => {
        update({
            ...values,
            condicaoPagamentoLoja: values.condicaoPagamentoLoja.map((loja) =>
                loja.loja.cdLoja === cdLoja ? { loja: loja.loja, condicaoPagamentoList: condicoesPagamento } : loja,
            ),
        });
    };

    return { onGatewayChange, onGeneralConditionsChange, addLojas, removeLojas, updateLojaConditions };
}
